import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Target, Zap, Shield, Radiation, AlertTriangle, Volume2 } from 'lucide-react';

export default function TutorialModal({ onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "🎯 OBJECTIVE: ELIMINATE ALL ENEMIES",
      icon: Target,
      content: (
        <div className="space-y-4">
          <p className="text-xl font-bold text-yellow-400">Win by being the last nation standing!</p>
          
          <div className="bg-black/50 p-4 rounded-lg border-2 border-red-600">
            <h3 className="text-lg font-bold text-red-400 mb-2">🏆 Victory Conditions:</h3>
            <ul className="space-y-2 text-gray-300">
              <li>✅ <strong>Eliminate all 4 AI countries</strong></li>
              <li>✅ Reduce their population below <strong>500,000</strong> (0.5M)</li>
              <li>✅ OR assassinate their leader with air strikes</li>
            </ul>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-yellow-600">
            <h3 className="text-lg font-bold text-yellow-400 mb-2">💀 Defeat Conditions:</h3>
            <ul className="space-y-2 text-gray-300">
              <li>❌ YOUR population drops below <strong>500,000</strong></li>
              <li>❌ YOUR leader gets assassinated</li>
            </ul>
          </div>

          <p className="text-sm text-gray-400 italic">
            💡 Tip: Balance aggression with defense! Going nuclear too early makes everyone hate you.
          </p>
        </div>
      )
    },
    {
      title: "💰 RESOURCES & TURN SYSTEM",
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-blue-600">
            <h3 className="text-lg font-bold text-blue-400 mb-3">📊 Your Resources:</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-green-400 font-bold">💵 MONEY</div>
                <div className="text-gray-300">Costs: Actions, military</div>
                <div className="text-xs text-gray-500">Passive: +1-3%/turn (based on pop)</div>
              </div>
              <div>
                <div className="text-red-400 font-bold">⚔️ MILITARY</div>
                <div className="text-gray-300">Power: Attacks, defense</div>
                <div className="text-xs text-gray-500">Passive: +1-3%/turn (based on pop)</div>
              </div>
              <div>
                <div className="text-purple-400 font-bold">📢 INFLUENCE</div>
                <div className="text-gray-300">Politics: Diplomacy, propaganda</div>
                <div className="text-xs text-gray-500">Passive: +0.5-1.5%/turn (based on pop)</div>
              </div>
              <div>
                <div className="text-yellow-400 font-bold">☢️ ENRICHMENT</div>
                <div className="text-gray-300">Nukes: Reach 100% to launch</div>
                <div className="text-xs text-gray-500">Passive: +7%/turn</div>
              </div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-purple-600">
            <h3 className="text-lg font-bold text-purple-400 mb-2">⚡ Buy Enrichment (Population-Based Pricing)</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>Click the lightning bolt ⚡ button to instantly buy +25% enrichment</div>
              <div className="bg-purple-900/30 p-2 rounded border border-purple-600 mt-2">
                <div className="text-yellow-400 font-bold text-xs mb-1">📊 Price scales with population:</div>
                <div className="text-xs text-gray-300 space-y-1">
                  <div>• <strong>Small nations (10M-50M):</strong> $800M - $900M</div>
                  <div>• <strong>Mid-size (100M-300M):</strong> $1,000M - $1,400M</div>
                  <div>• <strong>Large empires (500M+):</strong> $1,800M - $3,000M</div>
                </div>
                <div className="text-xs text-purple-300 mt-2 italic">
                  💡 Bigger populations = higher costs (prevents China from rushing nukes!)
                </div>
              </div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-2">🎮 Each Turn:</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li>✅ You get <strong>3 actions</strong> (green dots below your country)</li>
              <li>✅ Click any country to see action menu</li>
              <li>✅ After using 3 actions, click <strong>"END TURN"</strong></li>
              <li>✅ AI countries take their turns automatically</li>
              <li>✅ Resources regenerate passively each turn</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "🔊 AUDIO NOTIFICATIONS",
      icon: Volume2,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-blue-600">
            <h3 className="text-lg font-bold text-blue-400 mb-3">🎧 Listen for Important Updates!</h3>
            <p className="text-gray-300 mb-4">
              The game plays voice notifications when critical actions become available. Keep your sound on!
            </p>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-3">✈️ "AIR STRIKE AVAILABLE"</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>🎵 Plays when: Air Strike cooldown ends (Turn 2+ and 3 turns after last use)</div>
              <div className="text-yellow-400">💡 This means you can launch another air strike attack!</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-orange-600">
            <h3 className="text-lg font-bold text-orange-400 mb-3">🪖 "TROOP DEPLOYMENT AVAILABLE SIR"</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>🎵 Plays when: You reach Turn 2 and deployment is ready</div>
              <div className="text-yellow-400">💡 Deploy troops to drain enemy resources over 3 turns!</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-red-600">
            <h3 className="text-lg font-bold text-red-400 mb-3">☢️ "NUCLEAR LAUNCH AVAILABLE"</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>🎵 Plays when: Uranium enrichment reaches 100% (~Turn 15)</div>
              <div className="text-yellow-400">💡 You can now launch nuclear strikes! Use wisely.</div>
            </div>
          </div>

          <div className="bg-purple-900/30 p-4 rounded-lg border-2 border-purple-600">
            <p className="text-sm text-gray-300">
              <strong className="text-purple-400">🎮 Pro Tip:</strong> These audio cues help you stay on top of your strategy. 
              When you hear a notification, check your action menu to see what new options are available!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🛡️ POPULATION BUFFER & FORTIFICATIONS",
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-3">📈 What is the Buffer System?</h3>
            <p className="text-gray-300 mb-3">
              When your <strong className="text-green-400">population grows above its starting level</strong>, 
              your landmark becomes <strong>fortified</strong> with defensive structures!
            </p>
            <div className="bg-green-900/30 p-3 rounded border border-green-600">
              <div className="text-sm text-gray-300">
                <strong className="text-yellow-400">Buffer % = (Current Pop - Starting Pop) / Starting Pop × 100</strong>
              </div>
              <div className="text-xs text-gray-400 mt-2">
                Example: Start with 300M, grow to 345M → 15% buffer
              </div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-yellow-600">
            <h3 className="text-lg font-bold text-yellow-400 mb-3">🟡 LIGHT FORTIFICATIONS (0-30% Buffer)</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>• <strong>Sandbags</strong> appear around your landmark</div>
              <div>• Provides basic protection against attacks</div>
              <div className="text-yellow-400 italic">💡 How to build: Use Diplomacy or Train Military to boost population</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-orange-600">
            <h3 className="text-lg font-bold text-orange-400 mb-3">🟠 MEDIUM FORTIFICATIONS (30-60% Buffer)</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>• <strong>Sandbags + Watchtowers + Barbed Wire</strong></div>
              <div>• Enhanced defensive perimeter</div>
              <div className="text-orange-400 italic">💡 Keep using diplomacy and building military strength!</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-red-600">
            <h3 className="text-lg font-bold text-red-400 mb-3">🔴 HEAVY FORTIFICATIONS (60%+ Buffer)</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div>• <strong>Full military fortress</strong> with extensive defenses</div>
              <div>• Maximum protection for your landmark</div>
              <div className="text-red-400 italic">💡 This level of defense shows your nation's strength!</div>
            </div>
          </div>

          <div className="bg-purple-900/30 p-4 rounded-lg border-2 border-purple-600">
            <h3 className="text-sm font-bold text-purple-400 mb-2">🎯 STRATEGIC IMPORTANCE:</h3>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>• <strong>Buffer absorbs damage first</strong> - Your landmark stays pristine even when attacked!</li>
              <li>• <strong>Visual indicator</strong> - Fortified landmarks show you're thriving</li>
              <li>• <strong>Psychological warfare</strong> - Heavy fortifications intimidate enemies</li>
              <li>• <strong>Growth strategy</strong> - Prioritize population-boosting actions early game</li>
            </ul>
          </div>

          <div className="bg-blue-900/30 p-3 rounded border border-blue-600 mt-4">
            <p className="text-sm text-gray-300">
              <strong className="text-blue-400">💡 Pro Tip:</strong> Watch your landmark transform as you build buffer! 
              Fortifications appear automatically and scale with your population growth.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "⚔️ COMBAT ACTIONS & DAMAGE",
      icon: Zap,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-orange-600">
            <h3 className="text-lg font-bold text-orange-400 mb-3">✈️ AIR STRIKE</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-400">Cost:</div>
                <div className="text-white font-bold">15% Money + 10% Military + 10% Influence</div>
              </div>
              <div>
                <div className="text-gray-400">Damage:</div>
                <div className="text-red-400 font-bold">-20% Population</div>
              </div>
            </div>
            <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-600">
              <div className="text-red-400 font-bold text-xs">⚠️ ASSASSINATION CHANCE</div>
              <div className="text-gray-300 text-xs">
                If target has &lt;5M population: <strong>20% chance</strong> to kill their leader = instant win!
              </div>
            </div>
            <div className="mt-2 p-2 bg-purple-900/30 rounded border border-purple-600">
              <div className="text-purple-400 font-bold text-xs">📢 PROPAGANDA PENALTY</div>
              <div className="text-gray-300 text-xs">
                Air strikes reduce propaganda success vs target by <strong>-15%</strong> per attack
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-400">
              Relations: -15 | Cooldown: 3 turns if shot down
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-red-600">
            <h3 className="text-lg font-bold text-red-400 mb-3">☢️ NUCLEAR STRIKE</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-400">Requirement:</div>
                <div className="text-yellow-400 font-bold">100% Uranium Enrichment</div>
                <div className="text-xs text-gray-500">(+7% per turn = ~15 turns)</div>
              </div>
              <div>
                <div className="text-gray-400">Damage:</div>
                <div className="text-red-400 font-bold">-50% Population</div>
                <div className="text-red-400 font-bold">+ 5 Turns Fallout</div>
              </div>
            </div>
            <div className="mt-2 p-2 bg-red-900/50 rounded border border-red-700">
              <div className="text-red-300 font-bold text-xs">💀 CONSEQUENCES:</div>
              <ul className="text-gray-300 text-xs space-y-1 mt-1">
                <li>• All countries: Relations -50 (everyone hates you!)</li>
                <li>• Your population: -5% (refugees flee)</li>
                <li>• Cost: 25% of your money</li>
                <li>• Both countries enrichment → 0%</li>
                <li>• <strong className="text-purple-400">Propaganda -15%</strong> vs target (aggression penalty)</li>
              </ul>
            </div>
            <div className="mt-2 text-xs text-green-400">
              ✅ Unlocks Deploy Troops action after launch
            </div>
          </div>

          <div className="bg-black/50 p-3 rounded-lg border-2 border-blue-600">
            <h3 className="text-sm font-bold text-blue-400 mb-2">🪖 DEPLOY TROOPS</h3>
            <div className="text-xs text-gray-300">
              Cost: $60M + 30 Military + 15 Influence | Duration: 3 rounds<br/>
              Effect: Drain 5% resources/turn + 6.67% enrichment/turn = 20% total<br/>
              Cooldown: 2 turns | Max uses: 3 (then locked until nuke)<br/>
              <span className="text-purple-400 font-bold">Propaganda -15%</span> vs target (aggression penalty)
            </div>
          </div>
        </div>
      )
    },
    {
      title: "🎭 DIPLOMATIC ACTIONS",
      icon: AlertTriangle,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-purple-600">
            <h3 className="text-lg font-bold text-purple-400 mb-3">📢 PROPAGANDA</h3>
            <div className="text-sm text-gray-300">
              <div className="mb-2">Cost: <strong>$10M + 20 Influence</strong></div>
              <div>Success Chance: <strong>50%</strong> (affected by relations)</div>
              <div className="text-green-400 mt-2">✅ Success: +5 Influence, relations boost</div>
              <div className="text-red-400">❌ Fail: -15 relations, wasted resources</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-yellow-600">
            <h3 className="text-lg font-bold text-yellow-400 mb-3">💰 BLACKMAIL</h3>
            <div className="text-sm text-gray-300">
              <div className="mb-2">Cost: <strong>30 Influence</strong></div>
              <div>Success Chance: <strong>40%</strong> base</div>
              <div className="text-green-400 mt-2">
                ✅ Success: Steal $12M, +5 Influence, +8% Enrichment, +8% Propaganda
              </div>
              <div className="text-red-400">❌ Fail: -20 relations, expose yourself</div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-3">🤝 DIPLOMACY</h3>
            <div className="text-sm text-gray-300">
              <div className="mb-2">Cost: <strong>Variable Influence</strong></div>
              <div>Success Chance: <strong>50%</strong> (improved by your influence)</div>
              <div className="text-green-400 mt-2">
                ✅ Success: Major relations boost, +2% population, +5 Influence, +3% Enrichment
              </div>
              <div className="text-red-400">❌ Fail: Minimal relations boost, future attempts harder</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "🛰️ RADAR & INTELLIGENCE",
      icon: Zap,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-3">🛰️ INSTALL RADAR (One-Time Purchase)</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div className="mb-2">Cost: <strong className="text-yellow-400">$800M</strong> (fixed, one-time only)</div>
              <div className="text-green-400 font-bold">📡 BENEFITS:</div>
              <ul className="ml-4 space-y-1">
                <li>✅ <strong>Automatic intelligence reports</strong> each turn</li>
                <li>✅ Reveals <strong>2-3 random AI countries'</strong> resources</li>
                <li>✅ See their military strength & money</li>
                <li>✅ <strong>Permanent</strong> (until destroyed)</li>
              </ul>
              <div className="text-red-400 font-bold mt-3">⚠️ RISK:</div>
              <ul className="ml-4 space-y-1">
                <li>❌ <strong>15% destruction chance</strong> per air strike on you</li>
                <li>❌ Must rebuy for $800M if lost</li>
              </ul>
              <div className="bg-blue-900/30 p-3 rounded border border-blue-600 mt-3">
                <div className="text-blue-300 text-xs">
                  💡 <strong>Strategic Value:</strong> Know your enemies' strength before attacking! Radar pays for itself after ~2 turns of intel vs buying reports individually.
                </div>
              </div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-cyan-600">
            <h3 className="text-lg font-bold text-cyan-400 mb-2">🎯 How It Works:</h3>
            <div className="text-sm text-gray-300 space-y-2">
              <div><strong>1.</strong> Click your own country to open action panel</div>
              <div><strong>2.</strong> Click <strong className="text-green-400">"🛰️ INSTALL RADAR"</strong> button</div>
              <div><strong>3.</strong> Spinning radar dish appears on your country (map graphic)</div>
              <div><strong>4.</strong> Every turn: Automatic intel appears in action log (bottom-left)</div>
              <div className="text-yellow-400 text-xs italic mt-2">
                Example: "📡 AUTO INTEL: Russia: 150 military, $2500M | China: 200 military, $3000M"
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-900/50 to-cyan-900/50 p-4 rounded-lg border-2 border-green-600">
            <h3 className="text-lg font-bold text-green-400 mb-2">💡 Pro Tips:</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• Install radar early-game for maximum intel value</li>
              <li>• Check intel before attacking to avoid strong enemies</li>
              <li>• If radar destroyed, wait until you can afford $800M to rebuy</li>
              <li>• Radar is visible on map - enemies know you have it!</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "🛠️ TROUBLESHOOTING",
      icon: AlertTriangle,
      content: (
        <div className="space-y-4">
          <div className="bg-black/50 p-4 rounded-lg border-2 border-yellow-600">
            <h3 className="text-lg font-bold text-yellow-400 mb-3">⚠️ GAME UNRESPONSIVE? AI NOT PROGRESSING?</h3>
            <div className="text-sm text-gray-300 space-y-3">
              <p>
                If the game becomes stuck or AI turns stop progressing:
              </p>
              <ol className="space-y-2 ml-4 list-decimal">
                <li><strong className="text-green-400">Press ESC</strong> to open the pause menu</li>
                <li><strong className="text-blue-400">Click "Save and Exit"</strong> to save your current game</li>
                <li><strong className="text-purple-400">Return to the lobby</strong> and reload your saved game</li>
              </ol>
              <div className="mt-3 p-3 bg-green-900/30 rounded border border-green-600">
                <p className="text-green-300 text-xs">
                  ✅ <strong>This refreshes the game state</strong> and usually resolves stuck AI turns or unresponsive behavior.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border-2 border-blue-600">
            <h3 className="text-lg font-bold text-blue-400 mb-3">💾 SAVING YOUR PROGRESS</h3>
            <div className="text-sm text-gray-300">
              <p className="mb-2">Your game auto-saves after every action, but you can manually save anytime:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li>Press <strong className="text-yellow-400">ESC</strong> during gameplay</li>
                <li>Click <strong className="text-green-400">"Save and Exit"</strong></li>
                <li>Your progress is safely stored and can be loaded from the lobby</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 p-4 rounded-lg border-2 border-purple-600">
            <h3 className="text-lg font-bold text-purple-400 mb-2">🎮 OTHER TIPS:</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• Keep your browser tab active - backgrounding may pause animations</li>
              <li>• If audio stops working, refresh the page and reload your game</li>
              <li>• Use Chrome or Edge for best performance</li>
            </ul>
          </div>

          <div className="text-center pt-4">
            <p className="text-lg text-green-400 font-bold">Now you're truly ready! Good luck, Commander! 🎖️</p>
            <p className="text-sm text-gray-400 mt-2">Press "?" button anytime to review this tutorial</p>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark tutorial as completed
      localStorage.setItem('tutorial_completed', 'true');
      onComplete();
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tutorial_completed', 'true');
    onClose();
  };

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/95 backdrop-blur-sm">
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 border-4 border-blue-600 rounded-2xl max-w-4xl w-full mx-4 shadow-2xl"
           style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 bg-red-600 hover:bg-red-500 text-white rounded-full p-2 transition-all z-50"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Progress Indicator */}
        <div className="absolute top-4 left-4 text-blue-400 font-bold">
          Step {currentStep + 1} of {steps.length}
        </div>

        <div className="p-8 pt-12">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-blue-600 p-3 rounded-full">
              <CurrentIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-blue-400">
              {steps[currentStep].title}
            </h2>
          </div>

          {/* Content */}
          <div className="mb-8">
            {steps[currentStep].content}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6 border-t-2 border-blue-800">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                currentStep === 0
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            <button
              onClick={handleSkip}
              className="px-4 py-2 text-gray-400 hover:text-white transition-all"
            >
              Skip Tutorial
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold transition-all"
            >
              {currentStep === steps.length - 1 ? "Let's Play!" : 'Next'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {steps.map((_, index) => (
              <div
                key={`tutorial-step-${index}`}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-blue-500 w-8'
                    : index < currentStep
                    ? 'bg-green-500'
                    : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
